import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const faqs = [
  {
    q: "What is a cloud kitchen in Dubai?",
    a: "A cloud kitchen is a delivery-only commercial kitchen — no dine-in, no front-of-house, just production. Royal Express gives you a fully fitted, fully compliant kitchen unit in Dubai Investment Park, so you can cook, list on delivery apps and grow your brand without building a restaurant.",
  },
  {
    q: "Do I need to handle licensing and approvals myself?",
    a: "No. Every Royal Express kitchen unit is licensed, Dubai Municipality compliant and fire-safety approved before you arrive. You skip the fit-out, the paperwork maze and the months of waiting — just bring your pots and pans and start cooking.",
  },
  {
    q: "Where is Royal Express Cloud Kitchen located?",
    a: "Our flagship facility is the Royal Express Kitchen Units Center in Dubai Investment Park, Phase 2, Plot 597–775, Off Expo 2020 Road — with delivery coverage across Dubai South, Al Furjan, the Green Community, Jebel Ali Village and the Jumeirah Beach Residence area.",
  },
  {
    q: "Which delivery platforms can I sell on?",
    a: "All the majors — Talabat, Deliveroo and Noon. Our location sits inside one of Dubai's strongest delivery catchments, so your food reaches customers fast and fresh.",
  },
  {
    q: "What's included in a kitchen unit?",
    a: "Flexible 1–6 burner configurations, UNOX combi ovens, commercial extraction and ventilation, cold storage and freezers, prep lines, hygiene and wash stations — plus 24/7 secure access so you can cook on your own schedule.",
  },
  {
    q: "Who is Royal Express for?",
    a: "Home chefs taking the next step, start-ups testing a new concept, and established restaurant brands expanding into delivery across Dubai. Flexible rental plans mean you can start small and scale when the orders grow.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="border-t border-gold/15 py-24 md:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <Reveal className="mb-14 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold" data-testid="faq-eyebrow">FAQ</p>
          <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-white md:text-5xl" data-testid="faq-heading">
            Questions, <span className="italic text-gold">answered.</span>
          </h2>
        </Reveal>
        <div className="space-y-4">
          {faqs.map((f, i) => {
            const open = openIndex === i;
            return (
              <Reveal key={f.q} delay={i * 0.06}>
                <div
                  className={`overflow-hidden rounded-xl border transition-colors duration-500 ${open ? "border-gold/40 bg-gold/[0.05]" : "border-gold/15 bg-white/[0.02] hover:border-gold/30"}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenIndex(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left"
                    data-testid={`faq-question-${i}`}
                    aria-expanded={open}
                  >
                    <span className="font-serif text-lg font-medium text-white md:text-xl">{f.q}</span>
                    <motion.span
                      animate={{ rotate: open ? 45 : 0 }}
                      transition={{ duration: 0.35 }}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${open ? "border-gold bg-gold text-black" : "border-gold/40 text-gold"}`}
                    >
                      <Plus size={15} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className="px-6 pb-6 text-sm font-light leading-relaxed text-white/65 md:text-base" data-testid={`faq-answer-${i}`}>
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
